// src/pages/Campanhas.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PlusCircleIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';
import { isAdminOrSuperAdmin, getBearerHeaders } from '../lib/auth';
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
const selectClassName =
  "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
// Componente para o badge de status
const StatusBadge = ({ status }) => {
  const statusStyles = {
    em_fila: 'bg-yellow-500 text-yellow-100 border-yellow-600',
    reenviada: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-200 dark:border-orange-900',
    atendido: 'bg-green-500 text-green-100 border-green-600',
    falhou: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950 dark:text-rose-200 dark:border-rose-900',
    ativa: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-900',
    cancelada: 'bg-muted text-muted-foreground border-border',
    pausada: 'bg-amber-500 text-amber-100 border-amber-600',
    // Status antigos (compatibilidade com dados existentes)
    pendente: 'bg-yellow-500 text-yellow-100 border-yellow-600',
    concluída: 'bg-green-500 text-green-100 border-green-600',
    concluida: 'bg-green-500 text-green-100 border-green-600',
    sucesso: 'bg-green-500 text-green-100 border-green-600',
    erro: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950 dark:text-rose-200 dark:border-rose-900',
    em_progresso: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-900',
    em_andamento: 'bg-purple-500 text-purple-100 border-purple-600',
    enviado: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-900',
  };
  // Normalizar o status para lowercase para garantir que funcione com variações
  const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_') || '';
  return (
    <Badge className={cn("capitalize", statusStyles[normalizedStatus] || 'bg-muted text-muted-foreground border-border')}>
      {status}
    </Badge>
  );
};
export default function Campanhas() {
  const ITENS_POR_PAGINA = 10;
  const [campanhas, setCampanhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState('');
  const [filtroCriacaoInicio, setFiltroCriacaoInicio] = useState('');
  const [filtroCriacaoFim, setFiltroCriacaoFim] = useState('');
  const [filtroAgendamentoInicio, setFiltroAgendamentoInicio] = useState('');
  const [filtroAgendamentoFim, setFiltroAgendamentoFim] = useState('');
  const [clientes, setClientes] = useState([]);
  const [clienteFiltro, setClienteFiltro] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const clienteId = userData.clienteId || userData._id || userData.id;
  const isSuperAdmin = String(userData.role || '').trim().toLowerCase() === 'super_admin';
  const isAdmin = isAdminOrSuperAdmin(userData);
  const baseUrl = API_BASE_URL || 'https://ia.discador.net';
  // const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/';
  const tokenClient = localStorage.getItem('tokenClient');
  const getAuthConfig = () => {
    const h = getBearerHeaders();
    if (!h) return null;
    return { headers: h };
  };
  const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'object') {
      if (value._id) return String(value._id);
      if (value.id) return String(value.id);
    }
    return String(value);
  };
  const formatDateInput = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const parseDateString = (dateString) => {
    if (!dateString) return undefined;
    // Parse a string YYYY-MM-DD para Date no timezone local
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };
  const toRangeDate = (value, isEnd) => {
    if (!value) return null;
    // Parse usando timezone local para evitar problemas
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (isEnd) {
      date.setHours(23, 59, 59, 999);
    } else {
      date.setHours(0, 0, 0, 0);
    }
    return isNaN(date.getTime()) ? null : date;
  };
  const matchesDateRange = (value, start, end) => {
    const startDate = toRangeDate(start, false);
    const endDate = toRangeDate(end, true);
    if (!startDate && !endDate) return true;
    const date = value ? new Date(value) : null;
    if (!date || isNaN(date.getTime())) return false;
    if (startDate && date < startDate) return false;
    if (endDate && date > endDate) return false;
    return true;
  };
  const carregarCampanhas = useCallback(async () => {
    try {
      setLoading(true);
      const config = getAuthConfig();
      if (!config) {
        setError('Sessão expirada. Faça login novamente.');
        setLoading(false);
        return;
      }
      const params = !isAdmin && clienteId ? { clienteId } : {};
      if (tokenClient) {
        params.tokenClient = tokenClient;
      }
      const res = await axios.get(`${baseUrl}/api/campanhas`, {
        ...config,
        params,
      });
      setCampanhas(res.data);
      setError('');
    } catch (err) {
      setError('Erro ao carregar campanhas.');
      console.error('Erro ao carregar campanhas:', err?.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, [clienteId, isAdmin, tokenClient, baseUrl]);
  useEffect(() => {
    carregarCampanhas();
  }, [carregarCampanhas]);
  const carregarClientes = useCallback(async () => {
    if (!isAdmin) return;
    const config = getAuthConfig();
    if (!config) return;
    try {
      const res = await axios.get(`${baseUrl}/api/usuarios`, config);
      setClientes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erro ao carregar clientes:', err);
    }
  }, [isAdmin, baseUrl]);
  useEffect(() => {
    carregarClientes();
  }, [carregarClientes]);
  const excluirCampanha = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta campanha e todos os seus dados?')) {
      try {
        const config = getAuthConfig();
        if (!config) return;
        await axios.delete(`${baseUrl}/api/campanhas/${id}`, {
          ...config,
          data: { tokenClient },
        });
        carregarCampanhas();
      } catch (err) {
        console.error('Erro ao excluir campanha:', err);
      }
    }
  };
  const reenviarCampanha = async (id) => {
    if (window.confirm('Deseja reenviar as chamadas pendentes e com falha desta campanha?')) {
      try {
        const config = getAuthConfig();
        if (!config) return;
        await axios.post(`${baseUrl}/api/campanhas/${id}/reenviar`, { tokenClient }, config);
        await axios.post(`${baseUrl}/api/campanhas/${id}/reenviar-falhas`, { tokenClient }, config);
        alert('Comando de reenvio enviado com sucesso!');
        carregarCampanhas();
      } catch (err) {
        console.error('Erro ao reenviar campanha:', err);
        alert('Erro ao reenviar campanha.');
      }
    }
  };
  const exportar = (format) => {
    const dataToExport = campanhas.map(c => ({
      Nome: c.nome,
      Status: c.status,
      Contatos: c.contatos?.length || 0,
      Data_Criacao: new Date(c.criadoEm).toLocaleString(),
    }));
    if (format === 'xlsx') {
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Campanhas');
      XLSX.writeFile(wb, 'relatorio_campanhas.xlsx');
    } else if (format === 'pdf') {
      const doc = new jsPDF();
      doc.text('Relatório de Campanhas', 14, 16);
      autoTable(doc, {
        startY: 20,
        head: [['Nome', 'Status', 'Contatos', 'Data de Criação']],
        body: dataToExport.map(Object.values),
      });
      doc.save('relatorio_campanhas.pdf');
    }
  };
  const clientesMap = useMemo(() => (
    new Map(clientes.map(c => [String(c._id), c]))
  ), [clientes]);
  const campanhasFiltradas = useMemo(() => {
    const search = filtro.toLowerCase().trim();
    return campanhas.filter(c => {
      const nome = (c.nome?.toLowerCase() || '');
      const status = (c.status?.toLowerCase() || '');
      if (search && !nome.includes(search) && !status.includes(search)) {
        return false;
      }
      if (!matchesDateRange(c.criadoEm, filtroCriacaoInicio, filtroCriacaoFim)) {
        return false;
      }
      if (!matchesDateRange(c.agendamento, filtroAgendamentoInicio, filtroAgendamentoFim)) {
        return false;
      }
      if (isAdmin && clienteFiltro) {
        const campanhaClienteId = normalizeId(c.clienteId);
        if (campanhaClienteId !== clienteFiltro) return false;
      }
      return true;
    });
  }, [
    campanhas,
    filtro,
    filtroCriacaoInicio,
    filtroCriacaoFim,
    filtroAgendamentoInicio,
    filtroAgendamentoFim,
    isAdmin,
    clienteFiltro
  ]);
  const totalPaginas = useMemo(() => Math.max(1, Math.ceil(campanhasFiltradas.length / ITENS_POR_PAGINA)), [campanhasFiltradas.length]);
  const campanhasPaginadas = useMemo(() => {
    const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
    return campanhasFiltradas.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [campanhasFiltradas, paginaAtual]);
  useEffect(() => {
    setPaginaAtual(1);
  }, [filtro, filtroCriacaoInicio, filtroCriacaoFim, filtroAgendamentoInicio, filtroAgendamentoFim, clienteFiltro]);
  useEffect(() => {
    if (paginaAtual > totalPaginas) {
      setPaginaAtual(totalPaginas);
    }
  }, [paginaAtual, totalPaginas]);
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Gerenciador de Campanhas</h1>
          <p className="text-sm text-muted-foreground">Crie, acompanhe e exporte campanhas.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={() => exportar('xlsx')}>
            <ArrowDownTrayIcon className="h-5 w-5" /> Excel
          </Button>
          <Button variant="secondary" onClick={() => exportar('pdf')}>
            <ArrowDownTrayIcon className="h-5 w-5" /> PDF
          </Button>
          <Button onClick={() => navigate('/campanhas/nova')}>
            <PlusCircleIcon className="h-5 w-5" /> Nova Campanha
          </Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar por nome ou status..."
                value={filtro}
                onChange={e => setFiltro(e.target.value)}
                className="pl-10"
              />
            </div>
            {isAdmin && (
              <div className="flex-1">
                <select
                  value={clienteFiltro}
                  onChange={e => setClienteFiltro(e.target.value)}
                  className={selectClassName}
                >
                  <option value="">Todos os clientes</option>
                  {clientes.map(cliente => (
                    <option key={cliente._id} value={String(cliente._id)}>
                      {cliente.nome || cliente.name || cliente.email || String(cliente._id)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label className="text-xs text-muted-foreground">Criação - de</Label>
              <Input
                type="date"
                value={filtroCriacaoInicio}
                onChange={(e) => setFiltroCriacaoInicio(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Criação - até</Label>
              <Input
                type="date"
                value={filtroCriacaoFim}
                onChange={(e) => setFiltroCriacaoFim(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Agendamento - de</Label>
              <Input
                type="date"
                value={filtroAgendamentoInicio}
                onChange={(e) => setFiltroAgendamentoInicio(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Agendamento - até</Label>
              <Input
                type="date"
                value={filtroAgendamentoFim}
                onChange={(e) => setFiltroAgendamentoFim(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-4 font-semibold text-white">Nome da Campanha</th>
              <th className="p-4 font-semibold text-white">Status</th>
              <th className="p-4 font-semibold text-center text-white">Contatos</th>
              {isAdmin && <th className="p-4 font-semibold text-white">Cliente</th>}
              <th className="p-4 font-semibold text-center text-white">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isAdmin ? 5 : 4} className="text-center p-8 text-muted-foreground">Carregando campanhas...</td></tr>
            ) : error ? (
              <tr><td colSpan={isAdmin ? 5 : 4} className="text-center p-8 text-destructive">{error}</td></tr>
            ) : campanhasFiltradas.length === 0 ? (
              <tr><td colSpan={isAdmin ? 5 : 4} className="text-center p-8 text-muted-foreground">Nenhuma campanha encontrada.</td></tr>
            ) : (
              campanhasPaginadas.map((c) => (
                <tr
                  key={c._id}
                  className="border-b border-border transition-colors hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/campanhas/${c._id}`)}
                >
                  <td className="p-4 font-medium">{c.nome}</td>
                  <td className="p-4"><StatusBadge status={c.status} /></td>
                  <td className="p-4 text-center">{c.contatos?.length || 0}</td>
                  {isAdmin && (
                    <td className="p-4">
                      {(() => {
                        const clienteIdValue = normalizeId(c.clienteId);
                        const cliente = clientesMap.get(clienteIdValue);
                        return cliente?.nome || cliente?.name || cliente?.email || clienteIdValue || 'N/A';
                      })()}
                    </td>
                  )}
                  <td className="p-4 text-center flex justify-center items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" onClick={() => navigate(`/campanhas/${c._id}`)} title="Visualizar Detalhes">
                      <EyeIcon className="h-5 w-5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => navigate(`/campanhas/${c._id}/editar`)} title="Editar Campanha">
                      <PencilSquareIcon className="h-5 w-5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => reenviarCampanha(c._id)} title="Reenviar Pendentes e falhas">
                      <ArrowPathIcon className="h-5 w-5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => excluirCampanha(c._id)} title="Excluir Campanha">
                      <TrashIcon className="h-5 w-5" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
      {campanhasFiltradas.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Mostrando {campanhasPaginadas.length} de {campanhasFiltradas.length} campanhas
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
              disabled={paginaAtual === 1}
            >
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {paginaAtual} de {totalPaginas}
            </span>
            <Button
              variant="outline"
              onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))}
              disabled={paginaAtual === totalPaginas}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
